import { Button, Card, Col, Input, message, Modal, Row, Spin } from "antd";
import api from "../../api/axios";
import { useEffect, useState } from "react";
import { getAllPackagesByResellerId, sellPackage } from "../../redux/slices/packageSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { SellPackageRequest } from "../../models/SellPackageRequest";

interface Subscriber {
    subscriberId: number;
    imsi: string;
    iccid: string;
  }
  
  export default function TopupEsimOnly() {
    const dispatch = useDispatch<AppDispatch>();
    const { packages, loading: packagesLoading } = useSelector((state: RootState) => state.packages);
    const { user } = useSelector((state: RootState) => state.auth);
  
    const [sales, setSales] = useState<Subscriber[]>([]);
    const [selectedSim, setSelectedSim] = useState<Subscriber | null>(null);
    const [toppingUpSimId, setToppingUpSimId] = useState<number | null>(null);
    const [search, setSearch] = useState('');
    const [packageSearch, setPackageSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
  
    const fetchSims = async () => {
      setLoading(true);
      try {
        const response = await api.get('/pos/Resellers/getStockSalesSims');
        const sales: Subscriber[] = [];
  
        response.data.salesSims?.forEach((item) => {
          item.imsiList?.forEach((x: any) => {
            sales.push({ subscriberId: item.subscriberId, imsi: x?.imsi, iccid: x?.iccid });
          });
        });
  
        setSales(sales);
      } catch (error: any) {
        message.error(error.displayMessage || 'Failed to load eSIMs');
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchSims();
      dispatch(getAllPackagesByResellerId());
    }, [dispatch]);

    const filteredSims = sales.filter(sim =>
      [sim.imsi, sim.iccid, sim.subscriberId.toString()].some(val =>
        val?.toLowerCase().includes(search.toLowerCase())
      )
    );
  
    // const filteredSims = sales.filter(sim =>
    //   [sim.imsi, sim.iccid].some(val => val?.toLowerCase().includes(search.toLowerCase()))
    // );
    const filteredPackages = packages.filter(pkg =>
      pkg.prepaidPackageTemplateName?.toLowerCase().includes(packageSearch.toLowerCase())
    );
  
    const handleAssign = async (packageId: string, sim: Subscriber) => {
      const selectedPackage = packages.find(pkg => pkg.prepaidPackageTemplateId === packageId);
      if (!selectedPackage) return;
  
      Modal.confirm({
        title: 'Confirm Package Assignment',
        content: `Are you sure you want to assign "${selectedPackage.prepaidPackageTemplateName}" to IMSI ${sim.imsi}?`,
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          setAssigning(true);
          try {
            const payload: SellPackageRequest = {
              packageId: selectedPackage.prepaidPackageTemplateId,
              email: null,
              phoneNumber: null,
              iccid: sim.iccid,
              cost: selectedPackage.cost
            };
            await dispatch(sellPackage(payload)).unwrap();
            message.success('Package assigned successfully');
            setSelectedSim(null);
            setToppingUpSimId(null);
            fetchSims();
          } catch {
            message.error('Failed to assign package');
          } finally {
            setAssigning(false);
          }
        },
      });
    };
  
    return (
      <Row gutter={[16, 16]}>
        {/* SIM List */}
        <Col xs={24} md={8} style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <Input
            placeholder="Search IMSI or ICCID"
            value={search}
            onChange={(e) => {
              const value = e.target.value;
              setSearch(value);
              setSelectedSim(null);
              setToppingUpSimId(null);
            }}
            style={{ marginBottom: 16 }}
          />
  
          {loading ? (
            <Spin />
          ) : (
            filteredSims.map(sim => {
              const isToppingUp = toppingUpSimId === sim.subscriberId;
              const isDisabled = toppingUpSimId !== null && !isToppingUp;
  
              return (
                <Card
                  key={`${sim.subscriberId}-${sim.imsi}`}
                  style={{
                    marginBottom: 12,
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                  actions={
                    isToppingUp
                      ? [<Button onClick={() => { setToppingUpSimId(null); setSelectedSim(null); }}>Cancel</Button>]
                      : [<Button type="primary" onClick={() => { setToppingUpSimId(sim.subscriberId); setSelectedSim(sim); }}>Top Up</Button>]
                  }
                >
                  <div><strong>IMSI:</strong> {sim.imsi}</div>
                  <div><strong>ICCID:</strong> {sim.iccid}</div>
                  <div><strong>SubscriberId:</strong> {sim.subscriberId}</div>
                </Card>
              );
            })
          )}
        </Col>
  
        {/* Packages */}
        <Col xs={24} md={16}>
          <h2>Available Packages</h2>
          <Input
            placeholder="Search packages..."
            value={packageSearch}
            onChange={(e) => setPackageSearch(e.target.value)}
            style={{ marginBottom: 16 }}
          />
          {packagesLoading ? (
            <Spin />
          ) : (
            <Row gutter={[16, 16]}>
              {filteredPackages.map(pkg => (
                <Col key={pkg.id} xs={24} sm={12} lg={8}>
                  <Card
                    title={pkg.prepaidPackageTemplateName}
                    bordered
                    actions={[
                      <Button
                        type="primary"
                        loading={assigning}
                        disabled={!selectedSim || toppingUpSimId !== selectedSim.subscriberId}
                        onClick={() => handleAssign(pkg.prepaidPackageTemplateId, selectedSim!)}
                      >
                        Assign
                      </Button>
                    ]}
                  >
                    {user?.showPrices && (
                      <p>{(pkg.dataByte / (1024 * 1024)).toFixed(0)} MB</p>
                    )}
                    <p>{pkg.periodDays} Days</p>
                    {user?.showPrices && (
                      <p>{pkg.cost}€</p>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>
    );
  }